<template>
  <div class="row">
    <div class="col-12">
      <div class="card shadow-sm integration-card">
        <div class="card-body">
          <h5 class="mb-1">WebChat</h5>
          <p class="text-muted mb-4">
            Permite que sistemas externos gerem acessos ao atendimento por QR Code
          </p>

          <label class="font-weight-bold small mb-1" for="webchat-token">Credencial</label>
          <div class="input-group">
            <input
              id="webchat-token"
              ref="tokenInput"
              class="form-control token-input"
              type="text"
              readonly
              :value="apiToken || ''"
              placeholder="O token será exibido após clicar em Gerar nova credencial"
            >
            <div class="input-group-append">
              <button
                class="btn btn-outline-primary"
                type="button"
                :disabled="!apiToken || generating"
                @click="copyToken"
              >
                <i class="fa fa-copy mr-1"></i>
                Copiar
              </button>
            </div>
          </div>

          <p class="small text-muted mt-2 mb-4">
            A credencial existente não pode ser consultada. Ao gerar uma nova credencial, a anterior será invalidada.
          </p>

          <button
            class="btn btn-primary"
            type="button"
            :disabled="generating"
            @click="confirmGenerateToken"
          >
            <i class="fa fa-key mr-1"></i>
            {{ generating ? 'Gerando...' : 'Gerar nova credencial' }}
          </button>

          <div class="border-top mt-4 pt-4">
            <h6>Uso da credencial</h6>
            <p class="small mb-2">
              A credencial deve ser armazenada exclusivamente no servidor do sistema integrador e enviada no header Authorization.
            </p>
            <pre class="usage-example mb-0">POST /integrations/webchat/customer
Authorization: Bearer TOKEN_DA_INTEGRACAO
Content-Type: application/json

{
  "phone": "11999999999",
  "name": "João da Silva"
}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Integrations',

  data() {
    return {
      apiToken: null,
      generating: false
    }
  },

  methods: {
    confirmGenerateToken() {
      if (this.generating) {
        return;
      }

      this.$swal({
        title: 'Gerar nova credencial WebChat?',
        text: 'Se já existir uma credencial, ela será invalidada imediatamente. O sistema integrador deixará de funcionar até receber a nova credencial.',
        buttons: ['Cancelar', 'Gerar credencial'],
        dangerMode: true
      }).then(confirmed => {
        if (confirmed) {
          this.generateToken();
        }
      });
    },

    generateToken() {
      this.generating = true;

      this.$http.post('integrations/webchat/token', {}, {
        emulateJSON: false,
        headers: {
          'Content-Type': 'application/json'
        }
      }).then(response => {
        const data = response.data || {};

        if (data.success === true && typeof data.api_token === 'string' && data.api_token) {
          this.apiToken = data.api_token;
          this.$swal('Credencial WebChat gerada', 'Copie esta credencial agora. Por segurança, ela não poderá ser consultada novamente.', 'success');
          return;
        }

        this.showGenerationError('Não foi possível confirmar a geração da credencial. Tente novamente.');
      }, error => {
        this.handleRequestError(error);
      }).finally(() => {
        this.generating = false;
      });
    },

    handleRequestError(error) {
      if (!error || error.status === 0) {
        this.showGenerationError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.');
        return;
      }

      if (error.status === 403) {
        this.showGenerationError('Apenas administradores gerais podem gerenciar integrações.');
        return;
      }

      this.showGenerationError('Não foi possível gerar a credencial. Tente novamente.');
    },

    showGenerationError(message) {
      this.$swal('Erro', message, 'error');
    },

    async copyToken() {
      if (!this.apiToken) {
        return;
      }

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.apiToken);
        } else {
          this.copyTokenFallback();
        }

        this.$swal('Credencial copiada', 'Armazene-a em um local seguro.', 'success');
      } catch (error) {
        try {
          this.copyTokenFallback();
          this.$swal('Credencial copiada', 'Armazene-a em um local seguro.', 'success');
        } catch (fallbackError) {
          this.$swal('Não foi possível copiar', 'Selecione a credencial no campo e copie manualmente.', 'error');
        }
      }
    },

    copyTokenFallback() {
      const input = this.$refs.tokenInput;
      input.focus();
      input.select();
      input.setSelectionRange(0, input.value.length);

      if (!document.execCommand('copy')) {
        throw new Error('Cópia não suportada');
      }

      window.getSelection().removeAllRanges();
    }
  },

  beforeDestroy() {
    this.apiToken = null;
  }
}
</script>

<style scoped>
.integration-card {
  min-height: 100px;
}

.token-input {
  font-family: monospace;
}

.usage-example {
  padding: 12px;
  overflow-x: auto;
  border-radius: 4px;
  background: #f5f5f5;
  font-size: 12px;
  white-space: pre-wrap;
}
</style>
